import { t, useTranslation } from '../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import CountryFlag, { countryFromRegion, countryName } from '../../shared/components/CountryFlag';
import { useNavigate } from 'react-router-dom';
import {
    Steps, Card, Row, Col, Button, Form, Input, Radio, Tag, Empty, Spin, message, Alert, Descriptions, Typography, Segmented,
} from 'antd';
import { EnvironmentOutlined, AppstoreOutlined, SettingOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import provisionService from '../services/provisionService';
import cartService from '../services/cartService';
import { formatMoney, PLATFORM_CURRENCY } from '../../shared/utils/money';
import { statusLabel } from '../../shared/constants/statusDictionary';
import { localName } from '../../shared/utils/format';

const cycleText = (c) => t(statusLabel(c));
const CYCLE_UNIT = { monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年', biennially: '两年', triennially: '三年' };
const CYCLE_ORDER = ['monthly', 'quarterly', 'semi_annually', 'annually', 'biennially', 'triennially', 'onetime'];
const normCycle = (c) => String(c || '').replace(/-/g, '_');
const cycleUnit = (c) => (CYCLE_UNIT[normCycle(c)] ? t(CYCLE_UNIT[normCycle(c)]) : cycleText(c));

const { Title, Text } = Typography;

/**
 * 统一购买向导:选地区 → 选套餐 → 填该产品必要配置 → 加入购物车。
 * 用户不选具体节点(激活时后端 NodeAllocator 按地区自动分配)。
 * 各产品配置字段(资源规格由套餐决定,这里只收资源本征属性):
 */
const CONFIG_SCHEMA = {
    host_shared: {
        title: '共享主机',
        fields: [
            { name: 'domain', label: '主域名', rules: [{ required: true, message: '请输入主域名' }], placeholder: 'example.com' },
            { name: 'password', label: 'FTP 密码', type: 'password', rules: [{ required: true, min: 8, message: '至少 8 位' }] },
        ],
    },
    host: {
        title: '独享容器',
        fields: [
            { name: 'name', label: '容器名', rules: [{ required: true, pattern: /^[a-z0-9_-]{3,30}$/, message: '3-30 位小写字母/数字/_-' }], placeholder: 'mycontainer' },
        ],
    },
    yeapmail: {
        title: '域名邮箱',
        fields: [
            { name: 'domain', label: '邮箱域名', rules: [{ required: true, message: '请输入邮箱域名' }], placeholder: 'example.com' },
        ],
    },
    // 云盘：mode 在选套餐一步选（个人 / 组织），这里只收组织名
    disk: {
        title: '云盘',
        fields: [
            { name: 'org_name', label: '组织名称', rules: [{ required: true, message: '请输入组织名称' }], placeholder: 'Acme Inc.', showIf: (mode) => mode === 'org' },
        ],
    },
    evm: {
        title: '云服务器',
        fields: [
            { name: 'hostname', label: '主机名', rules: [{ required: true, message: '请输入主机名' }], placeholder: 'myvm' },
            { name: 'password', label: '密码', type: 'password', rules: [{ required: true, min: 8, message: '至少 8 位' }] },
        ],
    },
};

const parseFeatures = (f) => {
    if (typeof f === 'string') { try { return JSON.parse(f); } catch (_) { return {}; } }
    return f && typeof f === 'object' ? f : {};
};

// 云盘套餐按 features.disk_mode 分组：个人 → personal；组织 → org_pool / org_seat
const DISK_MODE_OF_PLAN = { personal: 'personal', org_pool: 'org', org_seat: 'org' };
const diskPlanMode = (p) => DISK_MODE_OF_PLAN[parseFeatures(p.features).disk_mode] || null;
const fmtDiskFeatures = (features) => {
    const f = parseFeatures(features);
    const out = [];
    if (f.disk_storage_gb) {
        const gb = Number(f.disk_storage_gb);
        out.push({ label: t('Storage'), value: gb >= 1024 && gb % 1024 === 0 ? `${gb / 1024} TB` : `${gb} GB` });
    }
    if (f.disk_seats) out.push({ label: t('Members'), value: String(f.disk_seats) });
    if (f.disk_mode === 'org_seat') out.push({ label: t('Seats'), value: t('Buy 5 GB / 10 GB seats as needed') });
    return out;
};

const FEATURE_LABELS = {
    disk_space: '磁盘', mysql: '数据库', bandwidth: '流量', cpu: 'CPU', memory: '内存', disk: '磁盘', ftp_quota: 'FTP', cron_quota: '定时任务',
    php: 'PHP', nodejs: 'Node.js', python: 'Python', domains: '域名', databases: '数据库', ssl: 'SSL',
    mail_users_limit: ['用户数'], mail_storage_gb: ['存储', 'GB'], mail_domains_limit: ['域名'], mail_alias_domains: ['别名域名'],
    mail_aliases_limit: ['别名'], mail_daily_send_limit: ['每日发信', '封'], mail_attachment_size_mb: ['附件', 'MB'],
    mail_webmail: 'Webmail', mail_imap_pop: 'IMAP/POP', mail_catch_all: '全收邮件', mail_api_access: 'API 访问', mail_auto_reply: '自动回复',
    mail_dkim_dmarc: 'DKIM/DMARC', mail_forwarding: '邮件转发', mail_smtp_relay: 'SMTP 中继', mail_virus_scan: '病毒扫描', mail_spam_filter: '垃圾邮件过滤',
};
// 数值/文字规格在前；布尔项只列支持的；未知的内部键（如 mail_xxx）不展示
const fmtFeatures = (features) => {
    if (!features || typeof features !== 'object') return [];
    const rows = Object.entries(features).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object');
    const label = (k) => { const m = FEATURE_LABELS[k]; return Array.isArray(m) ? m : [m || k]; };
    const known = (k) => FEATURE_LABELS[k] || !/_/.test(k);
    const values = rows.filter(([k, v]) => typeof v !== 'boolean' && known(k))
        .map(([k, v]) => { const [l, unit] = label(k); if (Number(v) === -1) return { label: t(l), value: t('Unlimited') }; return { label: t(l), value: unit ? `${v} ${t(unit)}` : String(v) }; });
    const flags = rows.filter(([k, v]) => v === true && known(k)).map(([k]) => ({ label: t(label(k)[0]), value: '✓' }));
    return [...values, ...flags];
};

export default function ProvisionWizard({ productType, giftGrantId = null, presetProductId = null, presetCycle = null, presetMode = null }) {
    const gift = !!giftGrantId && !!presetProductId;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const schema = CONFIG_SCHEMA[productType] || { title: productType, fields: [] };
    const isDisk = productType === 'disk';
    const [diskMode, setDiskMode] = useState(presetMode === 'org' ? 'org' : 'personal');
    const visibleFields = schema.fields.filter((f) => !f.showIf || f.showIf(diskMode));
    const [autoRegion, setAutoRegion] = useState(false);
    const pickRegionRef = React.useRef(null);

    const [step, setStep] = useState(0);
    const [regions, setRegions] = useState([]);
    const [region, setRegion] = useState(null);
    const [plans, setPlans] = useState([]);
    const [plan, setPlan] = useState(null);
    const [cycle, setCycle] = useState(null);
    const [loadingRegions, setLoadingRegions] = useState(false);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        let alive = true;
        setLoadingRegions(true);
        provisionService.getRegions(productType)
            .then((r) => {
                if (!alive) return;
                const list = r?.data || [];
                setRegions(list);
                // 云盘是全球单一地区（R2）：不让用户选，直接进入选套餐。其它产品即使只有一个地区也要让用户看到并确认。
                if (isDisk && list.length <= 1) { setAutoRegion(true); pickRegionRef.current(list[0]?.id ?? null); }
            })
            .catch(() => message.error(t('Failed to load regions')))
            .finally(() => { if (alive) setLoadingRegions(false); });
        return () => { alive = false; };
    }, [productType, isDisk]);

    const loadPlans = useCallback((r) => {
        setLoadingPlans(true);
        setPlans([]);
        provisionService.getPlans(productType, r)
            .then((res) => {
                // 价格按周期从短到长排（卡片显示最短周期价），未带币种的按 CNY 显示
                const list = (res?.data || []).map((p) => ({
                    ...p,
                    pricing: [...(p.pricing || [])]
                        .map((pr) => ({ ...pr, currency: pr.currency || PLATFORM_CURRENCY }))
                        .sort((a, b) => (CYCLE_ORDER.indexOf(normCycle(a.billing_cycle)) >>> 0) - (CYCLE_ORDER.indexOf(normCycle(b.billing_cycle)) >>> 0)),
                }));
                if (!gift) { setPlans(list); return; }
                // 赠品：只能开通赠送的套餐与周期，直接进入配置
                const p = list.find((x) => String(x.id) === String(presetProductId));
                setPlans(p ? [p] : []);
                if (p) { setPlan(p); setCycle(presetCycle || p.pricing?.[0]?.billing_cycle || null); setStep(2); }
            })
            .catch(() => message.error(t('Failed to load plans')))
            .finally(() => setLoadingPlans(false));
    }, [productType, gift, presetProductId, presetCycle]);

    // "<flag> United States · Seattle" from the region code + the API's English city name
    const regionLabel = (id) => {
        const r = regions.find((x) => x.id === id);
        const cc = countryFromRegion(id);
        const parts = [cc ? countryName(cc) : '', r && r.name && r.name !== id ? t(r.name) : (cc ? '' : id)].filter(Boolean);
        return parts.join(' · ') || id;
    };
    const pickRegion = (r) => { setRegion(r); setPlan(null); setCycle(null); loadPlans(r); setStep(1); };
    pickRegionRef.current = pickRegion;
    const shownPlans = isDisk && !gift ? plans.filter((p) => diskPlanMode(p) === diskMode) : plans;
    const pickPlan = (p) => {
        setPlan(p);
        setCycle(p.pricing?.[0]?.billing_cycle || null);
        setStep(2);
    };

    const handleAddToCart = async () => {
        try {
            // Step2 已校验过;此处表单可能已卸载(Step3),validateFields 只返回已挂载字段会丢配置,
            // 用 getFieldsValue(true) 取包含 preserved 的全部值
            const all = form.getFieldsValue(true);
            // 只提交当前可见的字段（云盘个人版不带 org_name）
            const config = {};
            visibleFields.forEach((f) => { if (all[f.name] !== undefined) config[f.name] = all[f.name]; });
            if (isDisk) config.mode = diskMode;
            const missing = visibleFields.filter((f) => f.rules?.some((r) => r.required) && !config[f.name]);
            if (missing.length > 0) {
                setStep(2);
                message.warning(t('Please fill in: {{fields}}', { fields: missing.map((f) => t(f.label)).join('、') }));
                return;
            }
            setSubmitting(true);
            await cartService.addItem({
                product_type: productType,
                product_id: plan.id,
                billing_cycle: cycle,
                quantity: 1,
                region,
                config,
                ...(gift ? { gift_grant_id: Number(giftGrantId) } : {}),
            });
            message.success(t('Added to cart'));
            navigate('/billing/cart');
        } catch (e) {
            if (e?.errorFields) return; // 表单校验失败
            message.error(e?.error || e?.message || t('Failed to add to cart'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <Title level={4}>{t('{{product}} — Activate', { product: t(schema.title) })}</Title>
            <Steps
                current={step}
                items={[
                    { title: t('Select region'), icon: <EnvironmentOutlined /> },
                    { title: t('Select plan'), icon: <AppstoreOutlined /> },
                    { title: t('Configuration'), icon: <SettingOutlined /> },
                    { title: t('Confirm'), icon: <ShoppingCartOutlined /> },
                ]}
                style={{ margin: '24px 0' }}
            />

            {/* Step 0: 地区 */}
            {step === 0 && (
                <Spin spinning={loadingRegions}>
                    {regions.length === 0 && !loadingRegions
                        ? <Empty description={t("No regions are available for this product yet (waiting for nodes to come online)")} />
                        : (
                            <Row gutter={[16, 16]}>
                                {regions.map((r) => (
                                    <Col xs={24} sm={12} md={8} key={r.id}>
                                        <Card hoverable onClick={() => pickRegion(r.id)} style={{ textAlign: 'center' }}>
                                            {countryFromRegion(r.id)
                                                ? <CountryFlag code={countryFromRegion(r.id)} size="lg" style={{ marginInlineEnd: 0, height: 24, width: 36 }} />
                                                : <EnvironmentOutlined style={{ fontSize: 24, color: '#1677ff' }} />}
                                            <div style={{ marginTop: 8, fontWeight: 600 }}>{regionLabel(r.id)}</div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                </Spin>
            )}

            {/* Step 1: 套餐 */}
            {step === 1 && (
                <Spin spinning={loadingPlans}>
                    {!autoRegion && <div style={{ marginBottom: 12 }}>{t('Region:')}<Tag color="blue"><CountryFlag code={countryFromRegion(region)} />{regionLabel(region)}</Tag><Button size="small" type="link" onClick={() => setStep(0)}>{t('Change')}</Button></div>}
                    {isDisk && !gift && (
                        <Segmented style={{ marginBottom: 16 }} value={diskMode} onChange={(v) => { setDiskMode(v); setPlan(null); }}
                            options={[{ value: 'personal', label: t('Personal') }, { value: 'org', label: t('Organization') }]} />
                    )}
                    {shownPlans.length === 0 && !loadingPlans
                        ? <Empty description={t("No plans are available in this region")} />
                        : (
                            <Row gutter={[16, 16]}>
                                {shownPlans.map((p) => (
                                    <Col xs={24} sm={12} md={8} key={p.id}>
                                        <Card hoverable title={localName(p.name)} onClick={() => pickPlan(p)}>
                                            <div style={{ fontSize: 20, color: '#cf1322', marginBottom: 8 }}>
                                                {formatMoney(p.pricing?.[0]?.price, p.pricing?.[0]?.currency)} <Text type="secondary" style={{ fontSize: 12 }}>/{cycleUnit(p.pricing?.[0]?.billing_cycle)}</Text>
                                            </div>
                                            {(isDisk ? fmtDiskFeatures(p.features) : fmtFeatures(p.features)).slice(0, 8).map((f) => (
                                                <div key={f.label} style={{ fontSize: 12, color: '#595959' }}>{f.label}: {f.value}</div>
                                            ))}
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                </Spin>
            )}

            {/* Step 2: 配置 */}
            {step === 2 && plan && (
                <div style={{ maxWidth: 480 }}>
                    <div style={{ marginBottom: 12 }}>
                        {!autoRegion && <Tag color="blue"><CountryFlag code={countryFromRegion(region)} />{regionLabel(region)}</Tag>}<Tag color="green">{localName(plan.name)}</Tag>
                        {gift ? <Tag color="gold">{t('Domain gift')}</Tag> : <Button size="small" type="link" onClick={() => setStep(1)}>{t('Change plan')}</Button>}
                    </div>
                    {plan.pricing?.length > 1 && !gift && (
                        <Form.Item label={t("Billing cycle")}>
                            <Radio.Group value={cycle} onChange={(e) => setCycle(e.target.value)}>
                                {plan.pricing.map((pr) => (
                                    <Radio.Button key={pr.billing_cycle} value={pr.billing_cycle}>{cycleText(pr.billing_cycle)} {formatMoney(pr.price, pr.currency)}</Radio.Button>
                                ))}
                            </Radio.Group>
                        </Form.Item>
                    )}
                    {!isDisk && <Alert type="info" showMessage={false} message={t("Resource specifications depend on the plan. The best node in your selected region will be assigned automatically.")} style={{ marginBottom: 16 }} />}
                    <Form form={form} layout="vertical">
                        {visibleFields.map((f) => (
                            <Form.Item key={f.name} name={f.name} label={t(f.label)} rules={f.rules?.map(rule => ({ ...rule, message: rule.message ? t(rule.message) : rule.message }))}>
                                {f.type === 'password' ? <Input.Password placeholder={f.placeholder} /> : <Input placeholder={f.placeholder} />}
                            </Form.Item>
                        ))}
                    </Form>
                    <Button type="primary" onClick={() => form.validateFields().then(() => setStep(3)).catch(() => {})}>{t('Next step')}</Button>
                </div>
            )}

            {/* Step 3: 确认 */}
            {step === 3 && plan && (
                <div style={{ maxWidth: 560 }}>
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label={t("Product")}>{t(schema.title)}</Descriptions.Item>
                        {isDisk && <Descriptions.Item label={t("Type")}>{diskMode === 'org' ? t('Organization') : t('Personal')}</Descriptions.Item>}
                        {!autoRegion && <Descriptions.Item label={t("Region")}><CountryFlag code={countryFromRegion(region)} />{regionLabel(region)}</Descriptions.Item>}
                        <Descriptions.Item label={t("Plan")}>{localName(plan.name)}</Descriptions.Item>
                        <Descriptions.Item label={t("Billing cycle")}>{cycleText(cycle)}</Descriptions.Item>
                        <Descriptions.Item label={t("Price")}>{(() => { const pr = plan.pricing.find((x) => x.billing_cycle === cycle) || plan.pricing[0]; return formatMoney(pr?.price, pr?.currency); })()}</Descriptions.Item>
                        {Object.entries(form.getFieldsValue()).map(([k, v]) => (
                            <Descriptions.Item key={k} label={t(schema.fields.find((f) => f.name === k)?.label || k)}>
                                {schema.fields.find((f) => f.name === k)?.type === 'password' ? '••••••••' : String(v ?? '')}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                    <div style={{ marginTop: 16 }}>
                        <Button style={{ marginRight: 8 }} onClick={() => setStep(2)}>{t('Back')}</Button>
                        <Button type="primary" loading={submitting} icon={<ShoppingCartOutlined />} onClick={handleAddToCart}>{t('Add to cart')}</Button>
                    </div>
                </div>
            )}
        </Card>
    );
}
